import { Settings } from "./settings";

/**
 * Cleopatra's Fire — Symbol Data
 *
 * Indices are the API contract: screens and the Hold & Win grid address
 * symbols by these numbers, sent as strings ("10"). Source:
 * docs/api-contract-cleopatras-fire.md §2 (dmon-rgsim draft v2). The PopiPlay
 * SYMBOL_MAP is identity, so these ARE the server's codes.
 *
 *   0     w0            wild
 *   1-4   h1-h4         high pays
 *   5-8   l1-l4         low pays (one `lp` skeleton, one skin each)
 *   9     coin          every coin value, jackpots included
 *   10    sc            scatter — reels 1, 3, 5 only
 *   11    blank         empty Hold & Win cell — never on the 5x4 screen
 *   12    chest         also what a pending chest draws as
 *   13    golden chest
 *   14    scarab
 *
 * 16 is CLIENT-ONLY. The server sends every coin as "9" and says which values
 * deserve jackpot art only through options.jackpots, so the jackpot coin art is
 * picked from the coin's value at runtime — not wired yet.
 *
 * Spines (checked against the files, 2026-09-28):
 *   h    high pays — skins h1-h4 on top of the default skin's `hp_stone`
 *   l    low pays — skins l1-l4; `win` only, no idle (GDD: lows have none)
 *   c    coin — default skin; darken / idle / in / win
 *   cj   jackpot coin — default skin is the coin, Jp_mini / Jp_minor / Jp_midi /
 *        Jp_major / Jp_grand / Jp_royal add the label; also `double_value`
 *   ch   BOTH chests — skins `silver` (chest) and `gold` (golden chest), NO
 *        default skin, so without one it draws nothing; close / closed_idle /
 *        in / open / open_idle, no `win`
 *   scr  scarab · sc scatter · w0 wild — default skin; idle / in / win
 * The engine applies `skin` with setSkinByName + setSlotsToSetupPose, and a
 * slot the named skin does not hold falls back to the default skin — which is
 * what puts the coin under the jackpot label and the stone under the high pay.
 */
export const symbolData = {
  // ── Paying symbols ──
  0: { name: "w0", spine: "w0", symbolPay: "wild", texture: "w0", value: null },
  // High pays share one skeleton and pick a skin out of it.
  1: { name: "h1", spine: "h", skin: "h1", symbolPay: "hp", texture: "h1", value: null },
  2: { name: "h2", spine: "h", skin: "h2", symbolPay: "hp", texture: "h2", value: null },
  3: { name: "h3", spine: "h", skin: "h3", symbolPay: "hp", texture: "h3", value: null },
  4: { name: "h4", spine: "h", skin: "h4", symbolPay: "hp", texture: "h4", value: null },
  // Low pays share one skeleton and pick a skin out of it.
  5: { name: "l1", spine: "l", skin: "l1", symbolPay: "lp", texture: "l1", value: null },
  6: { name: "l2", spine: "l", skin: "l2", symbolPay: "lp", texture: "l2", value: null },
  7: { name: "l3", spine: "l", skin: "l3", symbolPay: "lp", texture: "l3", value: null },
  8: { name: "l4", spine: "l", skin: "l4", symbolPay: "lp", texture: "l4", value: null },

  // ── Coin ──
  // The value is never here: it arrives per cell in bonus_data.cell_values
  // (points), and a coin that does not trigger carries no value at all.
  9: { name: "coin", spine: "c", symbolPay: "coin", texture: "c", value: null },

  // ── Scatter ──
  10: { name: "scatter", spine: "sc", symbolPay: "scatter", texture: "sc", value: null },

  // ── Hold & Win grid sprites ──
  // The grid arrives as kind names (bonus_data.cell_kinds); these are the
  // sprites for them. symbolPay keeps them out of the paytables and LOW_PAY.
  // No texture: in Cleopatra a blank is an EMPTY cell. `bl` is still the Mad
  // Labs rig (a teal panel), which drew over the H&W board (Vladimir,
  // 2026-09-28: "remove that"). The spine stays registered.
  // 11: { name: "blank", spine: "bl", symbolPay: "blank", texture: null, value: null },
  // `bl` is Cleopatra's own now (2026-09-30): the empty field, one `static`
  // pose — a TEXTURE only (the extractor poses `static`), never a spine on the
  // board (HoldAndWinOverlayCell.play still clears a blank). Vladimir: "enable
  // it, it has static animation just for texture".
  11: { name: "blank", spine: "bl", symbolPay: "blank", texture: "bl", value: null },
  12: { name: "chest", spine: "ch", skin: "silver", symbolPay: "chest", texture: "chest", value: null },
  13: { name: "golden_chest", spine: "ch", skin: "gold", symbolPay: "golden_chest", texture: "golden_chest", value: null },
  14: { name: "scarab", spine: "scr", symbolPay: "scarab", texture: "scr", value: null },

  // ── Client-only coin art (no server code) ──
  16: { name: "coin_jackpot", spine: "cj", symbolPay: "coin", texture: "cj", value: null },
};

// ─── Derived Constants ───

export const WILD_SYMBOL_ID = 0;
export const HIGH_PAY_SYMBOL_ID = 1;

export const SCATTER_SYMBOL_ID = 10;
export const SCATTER_SYMBOL_IDS = [SCATTER_SYMBOL_ID];

export const COIN_SYMBOL_ID = 9;
export const COIN_JACKPOT_SYMBOL_ID = 16;
export const COIN_SYMBOL_IDS = [COIN_SYMBOL_ID, COIN_JACKPOT_SYMBOL_ID];

export const BLANK_SYMBOL_ID = 11;
export const CHEST_SYMBOL_ID = 12;
export const GOLDEN_CHEST_SYMBOL_ID = 13;
export const SCARAB_SYMBOL_ID = 14;

// Jackpot name (the keys of options.jackpots) → `cj` skin. The skin is chosen
// at runtime from the coin's value; symbol 16 itself stays on the default skin.
export const JACKPOT_NAME_TO_SKIN = {
  mini: "Jp_mini",
  minor: "Jp_minor",
  midi: "Jp_midi",
  major: "Jp_major",
  grand: "Jp_grand",
  royal: "Jp_royal",
};

// ─── What may appear on a SPINNING reel ───
//
// The blur is cosmetic — the API decides what lands — but it must never show a
// symbol the math cannot produce.
//
//   0-8    paying symbols    yes
//   9      coin              yes — lands anywhere, base and free spins
//   10     scatter           no — reels 1, 3, 5 only, not expressible as a
//                            uniform filler; shown when it lands
//   11-14  H&W grid sprites  no — never on the 5x4 screen
//   16     client-only art   no
//
// Derived from symbolData, so a new symbol is EXCLUDED until someone states
// that it may spin.
const SPIN_FILLER_ALLOWED = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);

export const SPIN_FILLER_SYMBOL_IDS = Object.entries(symbolData)
  .filter(([id, data]) => data?.texture && SPIN_FILLER_ALLOWED.has(Number(id)))
  .map(([id]) => Number(id));

// ─── Not in this game ───
//
// Mad Labs' and Zeus's symbols. Cleopatra's Fire has none of them, so each is an
// explicit ABSENCE — null / [] / {} — rather than a value.
//
// They cannot simply be deleted: this build treats a missing named export as a
// webpack ERROR, even when the importing file is a feature this game never
// renders. An entry lives here until its last IMPORTER is deleted.
//
// The sentinels are also the safer answer: `id === null` is false for every real
// symbol, while a deleted export reads as `undefined`, and `undefined ===
// undefined` is TRUE — a false positive waiting to happen.
//
// Consume them defensively — `?? []`, `?? null`, `?.` — and never call a method
// on one at MODULE level.

// Mad Labs
export const MYSTERY_SYMBOL_ID = null;
export const GOLDEN_FRAME_SYMBOL_ID = null;
export const BONUS_SYMBOL_ID = null;
export const BONUS_SYMBOL_IDS = [];
export const SCATTER_1_SYMBOL_ID = null;
export const SCATTER_2_SYMBOL_ID = null;
export const SCATTER_3_SYMBOL_ID = null;
export const SCATTER_BLANK_SYMBOL_ID = null;
export const GAMBLE_LOW_SYMBOL_ID = null;
export const GAMBLE_MID_SYMBOL_ID = null;
export const GAMBLE_HIGH_SYMBOL_ID = null;
export const COLLECT_SYMBOL_ID = null;
export const MULTIPLIER_SYMBOL_ID = null;
export const MULTIPLIER_VALUE_TO_SKIN = {};

// Zeus
export const ROYAL_SYMBOL_ID = null;
export const JACKPOT_SYMBOL_IDS = [];
export const ZEUS_DOUBLE_ID = null;
export const ZEUS_MYSTERY_ID = null;
export const ZEUS_COLLECT_ID = null;
export const CD_MULT_ID = null;
export const CM_MINOR_ID = null;
export const CM_MINI_ID = null;
export const CM_MAJOR_ID = null;
export const JACKPOT_VALUE_TO_ID = {};

// ─── Helper Functions ───

export const isCoinSymbol = (id) => COIN_SYMBOL_IDS.includes(id);
export const isBonusSymbol = () => false;
export const isMysterySymbol = () => false;
export const isScatterSymbol = (id) => SCATTER_SYMBOL_IDS.includes(id);
// Jackpot art is a coin VALUE here (options.jackpots), not a symbol id.
export const isJackpotSymbol = () => false;
export const isRoyalSymbol = () => false;
export const getJackpotTier = () => null;

export const getCoinValue = (id) => {
  const sym = symbolData[id];
  return sym?.value ?? null;
};

/**
 * Base-game render priority, keyed by symbolPay so this stays data-driven —
 * add a symbol to symbolData and it inherits the right layer automatically.
 *
 * Symbols within one category deliberately share a value. Ties fall back to
 * natural left-to-right reel order.
 */
const BASE_PAY_Z_INDEX = {
  scatter: 9,
  wild: 8,
  hp: 7,
  lp: 5,
};

/**
 * Overlay z-index for a flat overlay — one container, every cell a sibling.
 * Higher value renders on top. PIXI only sorts among siblings, which is why
 * the overlay is flat in the first place.
 *
 *   coin(60) > scatter(9) > wild(8) > high pay(7) > low pay(5) > everything else(1)
 */
/**
 * ── Jackpot coins by tier (2026-10-06) ── a lower jackpot never draws over a
 * higher one: added to the coin's own depth, by the coin's skin. Mini 1 …
 * Grand 5 (Royal 6, should it ever land). Base, free spins and Hold & Win.
 */
export const JACKPOT_SKIN_RANK = {
  Jp_mini: 1,
  Jp_minor: 2,
  Jp_midi: 3,
  Jp_major: 4,
  Jp_grand: 5,
  Jp_royal: 6,
};

export const getSymbolZIndex = (id) => {
  if (isCoinSymbol(id)) return 60;

  const basePayZIndex = BASE_PAY_Z_INDEX[symbolData[id]?.symbolPay];
  if (basePayZIndex !== undefined) return basePayZIndex;

  return 1;
};

/**
 * A board cell's zIndex: its layer (getSymbolZIndex, + a jackpot's rank),
 * then, inside the layer:
 *
 *   value  a plain coin with a higher value sits over a lower one
 *   row    then the lower row on top
 *   reel   then the later reel
 *
 * All below 1, so nothing crosses a layer (every jackpot coin stays over every
 * plain coin). The order must be explicit: PIXI's sort re-orders the children
 * array in place, so equal zIndex falls back to whatever order the LAST sort
 * left — stacked symbols drew in a random order.
 *
 * The value step maps any value into [0, 0.9) and keeps the order; it
 * outweighs the row and reel steps for any two values a coin can show.
 */
const COIN_VALUE_Z_HALF = 1000;
export const cellZIndex = (layer, reelIndex = 0, row = 0, value = 0) =>
  layer +
  (value > 0 ? (0.9 * value) / (value + COIN_VALUE_Z_HALF) : 0) +
  Math.max(0, row) / 1e6 +
  Math.max(0, reelIndex) / 1e7;

// Low-pay symbol IDs — for the default slotface
export const LOW_PAY_SYMBOL_IDS = Object.entries(symbolData)
  .filter(([, d]) => d.symbolPay === "lp")
  .map(([id]) => parseInt(id));

/**
 * Low-pay ids that can NOT drop in free spins.
 *
 * Free spins run on their own reel set with no low symbols at all (GDD §7.2,
 * contract §8.1), at every point of the pack — so the answer is all four,
 * whatever the level argument the free-spins reel still passes.
 */
export const convertedLowSymbolIds = () => LOW_PAY_SYMBOL_IDS;

/**
 * Default slotface — a low-pay grid so nothing pays on load.
 */
export const DEFAULT_SLOTFACE = (() => {
  const { REEL_COUNT, SYMBOLS_VISIBLE } = Settings;
  const lp = LOW_PAY_SYMBOL_IDS;
  return Array.from({ length: REEL_COUNT }, (_, ri) =>
    Array.from(
      { length: SYMBOLS_VISIBLE },
      (_, si) => lp[(ri + si) % lp.length],
    ),
  );
})();
